import { contextWithCalculatedColumns } from "./calculatedColumns";
import { buildWithRelatedLists, relatedFromSql, relatedListNotIn, relatedValueSql } from "./relatedTables";
import type { ReportLogicContext } from "./context";
import type { DataColumn, MetricSection, PlanConfig, ReportConfig } from "../types";
import {
    allColumnsForModel,
    columnMatchesKey,
    columnsForModel,
    detailColumnsForModel,
    findColumnByIdentity,
} from "./catalogResolution";
import { reportGlobalFilters } from "./metrics";
import { inClause, notInClause, sqlAlias, sqlCommentText, sqlLiteral } from "./sqlExpressions";
import {
    breakdownListDetail,
    englishExcludedWhen,
    englishPhrase,
    tripDataEnglish,
    metricFilterExplainedCondition,
    ruleSetExplainedConditions,
    tripDataExplained,
    valueDetail,
} from "./exclusionReasonsPreview";
import { detailRuleSetOutputs, ruleSetCaseExpr } from "./detailRuleSets";
import { usesDetailRuleSetList } from "./detailRuleSetSelection";
import { combinedRuleSetIds, usesCombinedRuleSets } from "./combinedRuleSetSelection";
import { stripSqlLineComments } from "./combinedRuleSets";
import { buildRuleFilterClauses } from "./ruleInterpretation";
import { periodDatePeriodClauses } from "./periodDate";
import { metricOverridesReportPeriod, metricPeriodFilterClauses, modelAppliesReportingPeriod } from "./periodBounds";
import { periodClauseForModel, sectionClientScope } from "./sqlPreview";

// Preview of the "Download Excluded Records" queries. Mirrors
// Workbook/ReportSqlBuilder.Exclusions.cs (BuildExclusionSqlForSectionAsync), the same way
// sqlPreview.ts mirrors ReportSqlBuilder.BuildSqlForSectionAsync:
//
//   kept       : reporting period (or Period Date) + health-plan scope
//   negated    : rule-set filters, report member/invoice exclusions, Trip Data filters,
//                breakdown include/exclude values, metric filters
//
// A row is listed when it is in the kept scope and fails at least one negated condition.
// Like the other previews, values are shown as literals; the server binds them as
// parameters and uses technical column aliases ([detail:<id>]) instead of labels.

interface ExclusionCondition {
    label: string;
    expr: string;
    // Text expression for the Reason Details column (see exclusionReasonsPreview.ts).
    detail: string;
    // The condition in plain English, following "A record is excluded when ...".
    english?: string;
}

// One row per record: related-table columns are listed and tested with EXISTS
// (logic/relatedTables.ts), as in ReportSqlBuilder.Exclusions.cs.
function buildExclusionSqlForSection(context: ReportLogicContext, plan: PlanConfig, report: ReportConfig, section: MetricSection, useIds = false) {
    context = contextWithCalculatedColumns(context, report);
    return buildWithRelatedLists(() => buildExclusionSqlForSectionCore(context, plan, report, section, useIds));
}

function buildExclusionSqlForSectionCore(context: ReportLogicContext, plan: PlanConfig, report: ReportConfig, section: MetricSection, useIds = false) {
    const name = section.name || "Untitled metric";
    if (section.displayAs === "otp") return `-- "${sqlCommentText(name)}": excluded records are not available for On-Time Performance metrics yet.`;
    if (!section.model || !section.breakdownColumn) return `-- "${sqlCommentText(name)}": pick a column to see its excluded-records query.`;
    const model = context.dataModels[section.model];
    if (!model) return `-- "${sqlCommentText(name)}": selected data model is not available.`;
    const column = columnsForModel(context, section.model).find((item) => columnMatchesKey(item, section.breakdownColumn));
    if (!column) return `-- "${sqlCommentText(name)}": selected column is not available.`;

    const header = [
        `-- Excluded records for metric: ${sqlCommentText(name)}`,
        `-- Source: ${sqlCommentText(model.label)} (${model.table})`,
        "-- Same reporting period and health plan as the metric; rows that fail at least one of its other conditions.",
    ];

    const conditions: ExclusionCondition[] = [];

    // Rule-set filters
    if (usesDetailRuleSetList(section)) {
        detailRuleSetOutputs(context, report, section, useIds, column).forEach((output) => {
            if (!output.ruleSet) {
                header.push("-- A selected categorization rule set no longer exists.");
                return;
            }
            conditions.push(...ruleSetExplainedConditions(context, report, output.ruleSet, useIds));
        });
    } else if (usesCombinedRuleSets(section)) {
        // Combined rule sets: a row is excluded by them only when NONE of them counts it -
        // each rule set's own filters and CASE decide that (mirrors
        // ReportSqlBuilder.CombinedRuleSetsCountedExpr).
        const counted = combinedRuleSetIds(section).map((id) => {
            const ruleSet = report.bucketRuleSets.find((set) => set.id === id);
            const caseExpr = ruleSet ? ruleSetCaseExpr(context, report, ruleSet, useIds, column) : null;
            if (!ruleSet || !caseExpr) return null;
            const parts = buildRuleFilterClauses(context, report, ruleSet, useIds).map((clause) => stripSqlLineComments(clause).trim()).filter(Boolean)
                .concat([`(${stripSqlLineComments(caseExpr).trim()}) IS NOT NULL`]);
            return `(${parts.join(" AND ")})`;
        }).filter(Boolean) as string[];
        if (counted.length) {
            conditions.push({
                label: "Not counted by any of the combined rule sets",
                expr: `(${counted.join(" OR ")})`,
                detail: `N${sqlLiteral("Not counted by any of the combined rule sets")}`,
                english: "none of the metric's combined rule sets counts it",
            });
        }
    } else if (section.breakdownSource === "ruleSet") {
        const ruleSet = report.bucketRuleSets.find((set) => set.id === section.ruleSetId);
        if (!ruleSet) header.push("-- Pick a rule set for this metric to finish this query.");
        else conditions.push(...ruleSetExplainedConditions(context, report, ruleSet, useIds));
    }

    // Kept scope: reporting period + health plan
    const kept = metricOverridesReportPeriod(section)
        ? metricPeriodFilterClauses(context, report, section, useIds)
        : (modelAppliesReportingPeriod(report, section.model) ? periodDatePeriodClauses(context, report, section, useIds) : null)
            || [periodClauseForModel(context, report, section.model)];
    const clientScope = sectionClientScope(context, plan, section.model, section);
    if (clientScope) kept.push(clientScope);

    // Report-level exclusions
    const filters = reportGlobalFilters(report);
    const modelColumns = allColumnsForModel(context, section.model);
    const memberColumn = findColumnByIdentity(modelColumns, ["memberId", "member id"]);
    if (memberColumn && filters.exclusions.memberIds.length) {
        conditions.push({ label: "Member is on the report's excluded members list", expr: relatedListNotIn(memberColumn, filters.exclusions.memberIds), detail: valueDetail(memberColumn, "members"), english: `its ${memberColumn.label} is on the report's excluded members list (${filters.exclusions.memberIds.length.toLocaleString("en-US")} member${filters.exclusions.memberIds.length === 1 ? "" : "s"})` });
    }
    const invoiceColumn = findColumnByIdentity(modelColumns, ["invoiceId", "invoice id", "invoiceNumber", "invoice number", "invoiceNo", "invoice no"]);
    if (invoiceColumn && filters.exclusions.invoiceNumbers.length) {
        conditions.push({ label: "Invoice is on the report's excluded invoices list", expr: relatedListNotIn(invoiceColumn, filters.exclusions.invoiceNumbers), detail: valueDetail(invoiceColumn, "invoices"), english: `its ${invoiceColumn.label} is on the report's excluded invoices list (${filters.exclusions.invoiceNumbers.length.toLocaleString("en-US")} invoice${filters.exclusions.invoiceNumbers.length === 1 ? "" : "s"})` });
    }

    // Trip Data filters (transportation only - tripDataExplained returns null otherwise)
    const tripInclude = tripDataExplained(context, report, section.model, "include", useIds);
    if (tripInclude) conditions.push({ label: "Did not match the report's Trip Data include filter", expr: tripInclude.bool, detail: tripInclude.fail, english: tripDataEnglish(context, report, section.model, "include") || undefined });
    const tripExclude = tripDataExplained(context, report, section.model, "exclude", useIds);
    if (tripExclude) conditions.push({ label: "Matched the report's Trip Data exclude filter", expr: `NOT (${tripExclude.bool})`, detail: tripExclude.match, english: tripDataEnglish(context, report, section.model, "exclude") || undefined });

    // Metric filters
    const breakdownLabel = column.label || column.key;
    // A combined metric's breakdown column is only the ELSE fallback, so its value lists don't apply.
    const usesValueLists = !usesCombinedRuleSets(section);
    if (usesValueLists && section.include.length) conditions.push({ label: `${breakdownLabel} is not one of the metric's included values`, expr: inClause(context, section.model, column, section.include, useIds), detail: breakdownListDetail(column, section.include, true), english: `${breakdownLabel} ${englishPhrase("not_in", section.include, null, column)} (the metric's included values)` });
    if (usesValueLists && section.exclude.length) conditions.push({ label: `${breakdownLabel} is one of the metric's excluded values`, expr: notInClause(context, section.model, column, section.exclude, useIds), detail: breakdownListDetail(column, section.exclude, false), english: `${breakdownLabel} ${englishPhrase("in", section.exclude, null, column)} (the metric's excluded values)` });
    const includeFilter = metricFilterExplainedCondition(context, report, section, "filter", useIds);
    if (includeFilter) conditions.push(includeFilter);
    const excludeFilter = metricFilterExplainedCondition(context, report, section, "excludeFilter", useIds);
    if (excludeFilter) conditions.push(excludeFilter);

    if (!conditions.length) {
        header.push("-- This metric has no conditions other than reporting period and health plan, so no records are excluded.");
        return header.join("\n");
    }

    const detailColumns: DataColumn[] = detailColumnsForModel(context, section.model);
    if (!detailColumns.length) {
        header.push("-- No visible DataModelColumn records are configured for this data model.");
        return header.join("\n");
    }

    header.push(`-- Conditions checked: ${sqlCommentText(conditions.map((item) => item.label).join(" | "))}`);
    header.push(`-- In plain English: ${sqlCommentText(englishExcludedWhen(conditions.map((item) => item.english || item.label)))}`);

    const flag = (index: number) => `[__excl].[c${index + 1}]`;
    const reasonParts = conditions.map((item, index) => `    CASE WHEN ${flag(index)} = 0 THEN N${sqlLiteral(`; ${item.label}`)} ELSE N'' END`);
    const reasonExpr = `STUFF(CONCAT(\n${reasonParts.join(",\n")},\n    N''\n  ), 1, 2, N'')`;
    // Exclusion Reason: one line per failed condition, exactly what the row had and what was
    // needed (or what excluded it). "Excluded By" names the conditions.
    const detailParts = conditions.map((item, index) => `    CASE WHEN ${flag(index)} = 0 THEN CONCAT(NCHAR(10), ${item.detail}) ELSE N'' END`);
    const detailExpr = `STUFF(CONCAT(\n${detailParts.join(",\n")},\n    N''\n  ), 1, 1, N'')`;
    const selectItems = [`${detailExpr} AS ${sqlAlias("Exclusion Reason")}`, `${reasonExpr} AS ${sqlAlias("Excluded By")}`]
        .concat(detailColumns.map((item) => `${relatedValueSql(item)} AS ${sqlAlias(item.label || item.key)}`));
    // Each condition is followed by a line break: some clauses end in a "--" comment.
    const flagItems = conditions.map((item, index) => `  CASE WHEN ${item.expr}\n  THEN 1 ELSE 0 END AS [c${index + 1}]`);

    return [
        ...header,
        "SELECT",
        `  ${selectItems.join(",\n  ")}`,
        `FROM ${relatedFromSql(model)}`,
        "CROSS APPLY (SELECT",
        flagItems.join(",\n"),
        ") AS [__excl]",
        `WHERE ${kept.join("\n  AND ")}`,
        `  AND (${conditions.map((_, index) => `${flag(index)} = 0`).join(" OR ")});`,
    ].join("\n");
}

function buildExclusionSqlPreview(context: ReportLogicContext, plan: PlanConfig, report?: ReportConfig, useIds = false) {
    if (!report) return "-- Select a report to see its excluded-records queries.";
    if (!report.models.length) return "-- No Data Models attached to this report yet.";
    if (!report.sections.length) return "-- No metrics defined yet. Add a metric to see its excluded-records query.";

    // The download covers the metrics that are on the Excel workbook, in block order.
    const included = report.sections
        .filter((section) => section.excelLayout?.enabled)
        .sort((a, b) => (a.excelLayout?.blockOrder ?? 0) - (b.excelLayout?.blockOrder ?? 0));
    const leftOut = report.sections.filter((section) => !section.excelLayout?.enabled);

    const intro = [
        "-- Download Excluded Records: one sheet per metric on the Excel workbook.",
        "-- Each query keeps the metric's reporting period and health plan and lists the rows that fail any of its other conditions.",
        "-- The download caps each metric at 5,000,000 rows (SELECT TOP) and continues on extra sheets past Excel's 1,048,576-row limit.",
    ];
    if (leftOut.length) {
        intro.push(`-- Not in the download (Excel layout is off): ${sqlCommentText(leftOut.map((section) => section.name || "Untitled metric").join(", "))}`);
    }
    if (!included.length) {
        intro.push("-- No metric has its Excel layout turned on yet, so the download has no sheets.");
        return intro.join("\n");
    }

    return [intro.join("\n")]
        .concat(included.map((section) => buildExclusionSqlForSection(context, plan, report, section, useIds)))
        .join("\n\n");
}

export { buildExclusionSqlForSection, buildExclusionSqlPreview };
